/**
 * @file billLockDate.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description A new bill dated in a period Xero has locked (Jade, 5 Oct
 *              2026). The supplier's date — read from the upload, typed, or
 *              taken from a PO — that falls on or before the lock date
 *              becomes the first open day, and Add Bill says so in the window
 *              (never a dialog). The server refuses a locked date regardless
 *              (xeroLockService.assertOpen); this is the default that saves.
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** dd/mm/yyyy from YYYY-MM-DD (no time zone involved). */
export function ukDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
}

/**
 * The date a new bill takes: `date`, or the first open day when `date` is on
 * or before the lock.
 * @param {string} date — YYYY-MM-DD
 * @param {string|null} firstAllowedDate — YYYY-MM-DD (useXeroLock)
 * @returns {{ date: string, moved: boolean }}
 */
export function openBillDate(date, firstAllowedDate) {
  if (!ISO.test(String(date || '')) || !ISO.test(String(firstAllowedDate || ''))) {
    return { date, moved: false };
  }
  return date < firstAllowedDate ? { date: firstAllowedDate, moved: true } : { date, moved: false };
}

const WHOSE = {
  supplier: "The supplier's date",
  typed: 'The date typed',
  po: "The PO's date",
  copy: "The voided bill's date",
};

/**
 * The notice under the Bill Date: "The supplier's date (29/09/2026) is in a
 * locked period, so the bill is dated 01/10/2026, the first open day."
 * @param {{ from: string, to: string, source?: 'supplier'|'typed'|'po'|'copy' }} n
 */
export function lockedDateNotice({ from, to, source = 'typed' } = {}) {
  const whose = WHOSE[source] || WHOSE.typed;
  return `${whose} (${ukDate(from)}) is in a locked period, so the bill is dated ${ukDate(to)}, the first open day.`;
}
